/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 * Written by Hendrik, December 28, 2025.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './recents.css';

// Simple SVG Icon for Refresh
const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 4v6h-6"></path>
        <path d="M1 20v-6h6"></path>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
);

export default function Recent() {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const token = localStorage.getItem('auth_token');

    const loadActivity = async (showLoading = true) => {
        if (showLoading) setIsRefreshing(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_recent_activity.php`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            setActivities(response.data.activities || []);
        } catch (error) {
            setActivities([]);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadActivity(true);
        const interval = setInterval(() => loadActivity(false), 2 * 60 * 1000); // Poll every 2 mins
        return () => clearInterval(interval);
    }, [token]);

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'No date') return '-';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
            }).format(date);
        } catch { return '-'; }
    };

    const getRoleBadge = (role) => {
        const roleClass = role ? `role-badge ${role.toLowerCase()}` : 'role-badge unknown';
        return <span className={roleClass}>{role || 'Unknown'}</span>;
    };

    const getActionBadge = (actionType) => {
        const isUpdate = actionType === 'update';
        return (
            <span className={`status-badge ${isUpdate ? 'update' : 'add'}`}>
                {isUpdate ? 'Updated' : 'Added'}
            </span>
        );
    };

    return (
        <div className="recent-wrapper">
            <div className="recent-header">
                <div>
                    <h1>Recent Activity</h1>
                    <p className="subtitle">Real-time updates on student marks and changes</p>
                </div>
                <button
                    onClick={() => loadActivity(true)}
                    className={`btn-refresh ${isRefreshing ? 'spinning' : ''}`}
                    disabled={isRefreshing}
                >
                    <RefreshIcon />
                    {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            <div className="recent-card">
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading activity log...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="modern-table">
                            <thead>
                                <tr>
                                    <th>Student</th>
                                    <th>Subject & Section</th>
                                    <th>Modification</th>
                                    <th>User</th>
                                    <th>Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activities.length > 0 ? (
                                    activities.map((activity, index) => (
                                        <tr key={activity.id || index}>
                                            <td style={{ fontWeight: '600', color: '#2c3e50' }}>
                                                {activity.student_name}
                                            </td>
                                            <td>
                                                <div className="subject-cell">
                                                    <span className="subject-name">{activity.subject_name}</span>
                                                    {activity.grade_level && <span className="grade-tag">Gr {activity.grade_level}</span>}
                                                </div>
                                                <div className="section-name">{activity.section_name || 'General'}</div>
                                            </td>
                                            <td>
                                                <div className="change-cell">
                                                    {getActionBadge(activity.action_type)}
                                                    <div className="mark-change">
                                                        {activity.old_value != null && (
                                                            <>
                                                                <span className="old-val">{activity.old_value}</span>
                                                                <span className="arrow">→</span>
                                                            </>
                                                        )}
                                                        <span className="new-val">{activity.new_value}/{activity.max_mark}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="user-cell">
                                                    <span className="username">{activity.changed_by || 'Unknown'}</span>
                                                    {getRoleBadge(activity.user_role)}
                                                </div>
                                            </td>
                                            <td className="date-cell">{formatDate(activity.updated_at)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="empty-state">
                                            <p>No recent activity found.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}